import React, { useCallback, useState } from 'react'
import { CanAccess, useMenu } from '@refinedev/core'
import type { ITreeMenu } from '@refinedev/core'
import Collapse from '@mui/material/Collapse'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ListOutlinedIcon from '@mui/icons-material/ListOutlined'
import { Link as RouterLink } from 'react-router-dom'

const STORAGE_KEY = 'fs-admin-sidebar-groups-v1'

interface SidebarMenuProps {
  collapsed: boolean
}

type OpenState = Record<string, boolean>

function loadOpenState(): OpenState {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as OpenState) : {}
  } catch {
    return {}
  }
}

function saveOpenState(state: OpenState) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // ignore quota / disabled storage
  }
}

function hasActiveDescendant(
  item: ITreeMenu,
  selectedKey: string | undefined,
): boolean {
  if (!item.children?.length) return false
  return item.children.some(
    (child) => child.key === selectedKey || hasActiveDescendant(child, selectedKey),
  )
}

export const SidebarMenu: React.FC<SidebarMenuProps> = ({ collapsed }) => {
  const { menuItems, selectedKey } = useMenu()
  const [openGroups, setOpenGroups] = useState<OpenState>(loadOpenState)

  const toggleGroup = useCallback((name: string) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [name]: !prev[name] }
      saveOpenState(next)
      return next
    })
  }, [])

  const renderLeaf = (item: ITreeMenu, depth: number) => {
    const route = item.route ?? item.list
    if (!route || typeof route !== 'string') return null
    const label = item.label ?? item.name
    const active = item.key === selectedKey
    const icon = item.icon ?? <ListOutlinedIcon />

    return (
      <CanAccess
        key={item.key}
        resource={item.name}
        action="list"
        params={{ resource: item }}
      >
        <Tooltip
          title={label}
          placement="right"
          disableHoverListener={!collapsed}
          arrow
        >
          <ListItemButton
            component={RouterLink}
            to={route}
            selected={active}
            sx={{
              pl: collapsed ? 0 : 2 + depth * 2,
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: collapsed ? 0 : 40,
                justifyContent: 'center',
              }}
            >
              {icon}
            </ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary={label}
                primaryTypographyProps={{ noWrap: true, fontSize: '14px' }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </CanAccess>
    )
  }

  const renderItem = (item: ITreeMenu, depth: number): React.ReactNode => {
    const hasChildren = Boolean(item.children?.length)
    if (!hasChildren) {
      return renderLeaf(item, depth)
    }

    if (collapsed) {
      return (
        <React.Fragment key={item.key}>
          {item.children?.map((child) => renderItem(child, depth))}
        </React.Fragment>
      )
    }

    const label = item.label ?? item.name
    const icon = item.icon ?? <ListOutlinedIcon />
    const storedOpen = openGroups[item.name]
    const isOpen = storedOpen ?? hasActiveDescendant(item, selectedKey)

    return (
      <React.Fragment key={item.key}>
        <ListItemButton
          onClick={() => toggleGroup(item.name)}
          sx={{ pl: 2 + depth * 2 }}
        >
          <ListItemIcon sx={{ minWidth: 40, justifyContent: 'center' }}>
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={label}
            primaryTypographyProps={{ noWrap: true, fontSize: '14px' }}
          />
          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItemButton>
        <Collapse in={isOpen} unmountOnExit>
          <List disablePadding>
            {item.children?.map((child) => renderItem(child, depth + 1))}
          </List>
        </Collapse>
      </React.Fragment>
    )
  }

  return <>{menuItems.map((item) => renderItem(item, 0))}</>
}
